<template>
  <div class="fusco-gift-page">
    <header class="fusco-gift-header">
      <div class="fusco-gift-header-inner">
        <button type="button" class="fusco-gift-back" aria-label="Torna indietro" @click="goBack">
          <span class="material-icons fusco-gift-icon" aria-hidden="true">arrow_back</span>
        </button>
        <button type="button" class="fusco-gift-brand-button" aria-label="Fusco Fit, area personale" @click="goHome">
          <span class="fusco-gift-brand">
            <img class="fusco-gift-logo" src="@/assets/fusco-fit-logo-black.png" alt="Fusco Fit · Be better." />
          </span>
        </button>
        <span class="fusco-gift-header-label">Un regalo. Un nuovo inizio.</span>
        <button type="button" class="fusco-gift-account" aria-label="Area personale" @click="goHome">
          <span class="material-icons fusco-gift-icon" aria-hidden="true">person_outline</span>
          <span class="fusco-gift-account-label">Area personale</span>
        </button>
      </div>
    </header>

    <main class="fusco-gift-layout">
      <section class="fusco-gift-intro" aria-labelledby="fusco-gift-title">
        <p class="fusco-gift-eyebrow"><span class="fusco-gift-dot" aria-hidden="true"></span>Fusco Fit · Gift card</p>
        <h1 id="fusco-gift-title" class="fusco-gift-title">REGALA<br /><span class="fusco-gift-title-accent">una gift.</span></h1>
        <p class="fusco-gift-lead">Un pensiero per chi ami.<br />Il primo passo verso il suo benessere.</p>

        <div class="fusco-gift-preview" aria-label="Anteprima della gift card">
          <div class="fusco-gift-preview-top">
            <span class="fusco-gift-preview-brand">FUSCO FIT</span>
            <span class="fusco-gift-preview-tag">GIFT CARD</span>
          </div>
          <p class="fusco-gift-preview-motto">BE<br /><span class="fusco-gift-preview-motto-accent">BETTER.</span></p>
          <div class="fusco-gift-preview-bottom">
            <div class="fusco-gift-preview-dedication">
              <span class="fusco-gift-preview-caption">Un regalo per</span>
              <span class="fusco-gift-preview-recipient">{{ form.name || 'una persona speciale' }}</span>
            </div>
            <span class="fusco-gift-preview-value" :class="{ 'fusco-gift-preview-value--placeholder': !price }">{{ price ? formatPrice(price) : 'Scegli il valore' }}</span>
          </div>
        </div>

        <div class="fusco-gift-credit-note">
          <span class="material-icons fusco-gift-icon fusco-gift-credit-icon" aria-hidden="true">account_balance_wallet</span>
          <div class="fusco-gift-credit-copy">
            <h2 class="fusco-gift-credit-title">Tu scegli il valore. Noi lo dedichiamo a chi vuoi.</h2>
            <p class="fusco-gift-credit-text">Dopo la conferma del pagamento, l’importo viene aggiunto direttamente al saldo Fusco Fit del destinatario.</p>
          </div>
        </div>
      </section>

      <form @submit.prevent="onSubmit" class="fusco-gift-form" aria-label="Personalizza la tua gift">
        <div class="fusco-gift-form-heading">
          <p class="fusco-gift-eyebrow">Crea il tuo regalo</p>
          <span class="fusco-gift-step-count">{{ step }} / 3</span>
        </div>
        <ol class="fusco-gift-steps" aria-label="Passaggi del regalo">
          <li v-for="(label, index) in ['Destinatario', 'Pagamento', 'Riepilogo']" :key="label" class="fusco-gift-step" :class="{ 'fusco-gift-step--active': step === index + 1, 'fusco-gift-step--done': step > index + 1 }" :aria-current="step === index + 1 ? 'step' : undefined">
            <span class="fusco-gift-step-number" aria-hidden="true"><span v-if="step > index + 1" class="material-icons fusco-gift-icon">check</span><template v-else>0{{ index + 1 }}</template></span>
            <span class="fusco-gift-step-label">{{ label }}</span>
          </li>
        </ol>

        <div v-if="step === 1" class="fusco-gift-section">
          <div class="fusco-gift-section-heading">
            <h2 class="fusco-gift-section-title">Il tuo regalo, a modo tuo.</h2>
            <p class="fusco-gift-section-description">Scegli un importo e la persona a cui dedicarlo.</p>
          </div>
          <div class="fusco-gift-field">
            <label class="fusco-gift-label" for="gift-amount">Importo della gift *</label>
            <div class="fusco-gift-amount-field">
              <span class="fusco-gift-currency" aria-hidden="true">€</span>
              <input id="gift-amount" class="fusco-gift-input fusco-gift-input--amount" v-model.trim="form.amount" type="text" inputmode="decimal" required placeholder="0,00" aria-describedby="fusco-gift-amount-hint" />
            </div>
            <small id="fusco-gift-amount-hint" class="fusco-gift-hint">Importo libero in euro, maggiore di zero. Fino a due decimali.</small>
          </div>

          <div class="fusco-gift-field fusco-gift-recipient-field">
            <label class="fusco-gift-label" for="fusco-gift-email">Email del destinatario *</label>
            <div class="fusco-gift-inline">
              <input id="fusco-gift-email" class="fusco-gift-input" type="email" v-model.trim="form.email" required placeholder="persona@dominio.com" @blur="maybeLookupRecipient" />
              <button type="button" class="fusco-gift-button fusco-gift-button--secondary fusco-gift-lookup" @click="lookupRecipient" :disabled="lookup.loading">
                {{ lookup.loading ? 'Verifico…' : 'Controlla' }}
              </button>
            </div>
            <div class="fusco-gift-lookup-status" role="status">
              <small v-if="lookup.loading" class="fusco-gift-hint">Verifica in corso…</small>
              <small v-else-if="lookup.found" class="fusco-gift-hint fusco-gift-hint--ok">Utente trovato: {{ lookup.user?.name || '' }} {{ lookup.user?.surname || '' }}{{ lookup.user?.phone ? ' · ' + lookup.user.phone : '' }}</small>
              <small v-else-if="lookup.checked" class="fusco-gift-hint">Nuovo destinatario: inserisci i dati.</small>
              <small v-else class="fusco-gift-hint">Verifica l’email per completare i dati del destinatario.</small>
            </div>
          </div>
          <div class="fusco-gift-recipient-grid" v-if="showRecipientFields">
            <div class="fusco-gift-field">
              <label class="fusco-gift-label" for="fusco-gift-name">Nome *</label>
              <input id="fusco-gift-name" class="fusco-gift-input" v-model.trim="form.name" required placeholder="Nome destinatario" />
            </div>
            <div class="fusco-gift-field">
              <label class="fusco-gift-label" for="fusco-gift-surname">Cognome *</label>
              <input id="fusco-gift-surname" class="fusco-gift-input" v-model.trim="form.surname" required placeholder="Cognome destinatario" />
            </div>
            <div class="fusco-gift-field fusco-gift-field--full">
              <label class="fusco-gift-label" for="fusco-gift-phone">Telefono *</label>
              <input id="fusco-gift-phone" class="fusco-gift-input" v-model.trim="form.phone" type="tel" required placeholder="Telefono destinatario" />
              <small v-if="lookup.found && !lookup.user?.phone" class="fusco-gift-hint fusco-gift-hint--warn">Utente senza telefono: inserisci il numero.</small>
            </div>
          </div>
        </div>

        <div v-else-if="step === 2" class="fusco-gift-section">
          <div class="fusco-gift-section-heading">
            <h2 class="fusco-gift-section-title">Un pensiero che resta.</h2>
            <p class="fusco-gift-section-description">Aggiungi una dedica e scegli come pagare.</p>
          </div>
          <div class="fusco-gift-field">
            <label class="fusco-gift-label" for="fusco-gift-message">Messaggio per il destinatario <span class="fusco-gift-optional">(opzionale)</span></label>
            <textarea id="fusco-gift-message" class="fusco-gift-input fusco-gift-textarea" v-model.trim="form.message" rows="4" placeholder="Scrivi qui la tua dedica…"></textarea>
          </div>
          <fieldset class="fusco-gift-payment-fieldset">
            <legend class="fusco-gift-label">Metodo di pagamento *</legend>
            <div class="fusco-gift-payment-options">
              <label class="fusco-gift-payment" :class="{ 'fusco-gift-payment--active': form.paymentMethod === 'paypal' }">
                <input class="fusco-gift-radio" type="radio" value="paypal" v-model="form.paymentMethod" />
                <span class="fusco-gift-payment-copy"><span class="fusco-gift-payment-title">PayPal</span><span class="fusco-gift-payment-description">Pagamento immediato</span></span>
                <span class="material-icons fusco-gift-icon fusco-gift-payment-icon" aria-hidden="true">account_balance_wallet</span>
              </label>
              <label class="fusco-gift-payment" :class="{ 'fusco-gift-payment--active': form.paymentMethod === 'bonifico' }">
                <input class="fusco-gift-radio" type="radio" value="bonifico" v-model="form.paymentMethod" />
                <span class="fusco-gift-payment-copy"><span class="fusco-gift-payment-title">Bonifico</span><span class="fusco-gift-payment-description">Conferma dopo accredito</span></span>
                <span class="material-icons fusco-gift-icon fusco-gift-payment-icon" aria-hidden="true">account_balance</span>
              </label>
            </div>
          </fieldset>
        </div>

        <div v-else class="fusco-gift-section">
          <div class="fusco-gift-section-heading">
            <h2 class="fusco-gift-section-title">Il tuo regalo è quasi pronto.</h2>
            <p class="fusco-gift-section-description">Controlla i dettagli prima di confermare.</p>
          </div>
          <dl class="fusco-gift-summary">
            <div class="fusco-gift-summary-row"><dt class="fusco-gift-summary-label">Destinatario</dt><dd class="fusco-gift-summary-value">{{ form.name }} {{ form.surname }}<span class="fusco-gift-summary-email">{{ form.email }}</span></dd></div>
            <div class="fusco-gift-summary-row"><dt class="fusco-gift-summary-label">Telefono</dt><dd class="fusco-gift-summary-value">{{ form.phone }}</dd></div>
            <div class="fusco-gift-summary-row"><dt class="fusco-gift-summary-label">Pagamento</dt><dd class="fusco-gift-summary-value">{{ formatLabel(form.paymentMethod) }}</dd></div>
            <div class="fusco-gift-summary-row"><dt class="fusco-gift-summary-label">Regalo</dt><dd class="fusco-gift-summary-value">Gift card Fusco Fit</dd></div>
            <div class="fusco-gift-summary-row fusco-gift-summary-row--total"><dt class="fusco-gift-summary-label">Importo</dt><dd class="fusco-gift-summary-value">{{ formatPrice(price) }}</dd></div>
            <div v-if="form.message" class="fusco-gift-summary-row fusco-gift-summary-row--message"><dt class="fusco-gift-summary-label">La tua dedica</dt><dd class="fusco-gift-summary-value">{{ form.message }}</dd></div>
          </dl>
          <p class="fusco-gift-hint">L’importo verrà aggiunto al saldo del destinatario dopo la conferma del pagamento.</p>
        </div>

        <div class="fusco-gift-actions">
          <button type="button" class="fusco-gift-button fusco-gift-button--back" :disabled="loading || step === 1 || !!submission" @click="prevStep"><span class="material-icons fusco-gift-icon" aria-hidden="true">arrow_back</span>Indietro</button>
          <button v-if="step < 3" type="button" class="fusco-gift-button fusco-gift-button--primary" :disabled="loading" @click="nextStep">Avanti<span class="material-icons fusco-gift-icon" aria-hidden="true">arrow_forward</span></button>
          <button v-else-if="!completed" type="submit" class="fusco-gift-button fusco-gift-button--primary" :disabled="loading">{{ loading ? 'Salvataggio…' : submitLabel }}<span v-if="!loading" class="material-icons fusco-gift-icon" aria-hidden="true">arrow_forward</span></button>
        </div>

        <div v-if="error" class="fusco-gift-alert fusco-gift-alert--error" role="alert">{{ error }}</div>
        <div v-if="success" class="fusco-gift-alert fusco-gift-alert--success" role="status">{{ success }}</div>
        <p class="fusco-gift-form-note"><span class="material-icons fusco-gift-icon" aria-hidden="true">redeem</span>Un regalo su misura, in tre semplici passi.</p>
      </form>
    </main>
    <footer class="fusco-gift-footer"><span class="fusco-gift-footer-brand">FUSCO FIT</span><span>Be Better. Ogni giorno.</span></footer>
  </div>
</template>

<script>
import { getAuth } from 'firebase/auth';
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPayPalFunctionUrl } from '@/lib/env';
import { giftAmountCents } from '@/lib/giftCard';

export default {
  name: 'GiftOrder',
  data() {
    return {
      step: 1,
      form: {
        amount: '',
        email: '',
        name: '',
        surname: '',
        phone: '',
        message: '',
        paymentMethod: 'paypal',
      },
      lookup: {
        loading: false,
        found: false,
        checked: false,
        user: null,
        email: '',
      },
      loading: false,
      error: '',
      success: '',
      submission: null,
      createdOrder: null,
      completed: false,
    };
  },
  computed: {
    price() {
      return (giftAmountCents(this.form.amount) || 0) / 100;
    },
    submitLabel() {
      if (this.createdOrder?.paymentMethod === 'paypal') return 'Riprendi pagamento PayPal';
      return this.form.paymentMethod === 'bonifico' ? 'Registra ordine' : 'Paga con PayPal';
    },
    showRecipientFields() {
      if (!this.lookup.checked) return false;
      if (!this.lookup.found) return true;
      return !this.lookup.user?.phone || !this.lookup.user?.name || !this.lookup.user?.surname;
    },
  },
  watch: {
    'form.email'() {
      if (this.normalizeEmail(this.form.email) === this.lookup.email) return;
      this.lookup.checked = false;
      this.lookup.found = false;
      this.lookup.user = null;
      this.form.name = '';
      this.form.surname = '';
      this.form.phone = '';
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    goHome() {
      this.$router.push('/user');
    },
    formatPrice(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '€0,00';
      return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    },
    formatLabel(value) {
      if (!value) return '—';
      return value.toString().replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    normalizeEmail(v) {
      return (v || '').trim().toLowerCase();
    },
    async maybeLookupRecipient() {
      if (!this.form.email) return;
      await this.lookupRecipient();
    },
    async lookupRecipient() {
      const email = this.normalizeEmail(this.form.email);
      if (this.lookup.loading && this.lookup.email === email) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.error = "Inserisci un'email valida per il destinatario.";
        return;
      }
      this.lookup.loading = true;
      this.lookup.found = false;
      this.lookup.checked = false;
      this.lookup.user = null;
      this.lookup.email = email;
      this.error = '';
      try {
        const user = await this.findUserByEmail(email);
        if (email !== this.normalizeEmail(this.form.email)) return;
        if (user) {
          this.lookup.found = true;
          this.lookup.user = user;
          this.form.name = user.name || this.form.name;
          this.form.surname = user.surname || this.form.surname;
          this.form.phone = user.phone || this.form.phone;
        }
        this.lookup.checked = true;
      } catch (err) {
        if (email === this.normalizeEmail(this.form.email)) this.error = err?.message || 'Errore durante la verifica utente.';
      } finally {
        if (this.lookup.email === email) this.lookup.loading = false;
      }
    },
    async findUserByEmail(email) {
      const db = getFirestore();
      const trimmed = (email || '').trim();
      if (!trimmed) return null;
      const normalized = trimmed.toLowerCase();
      const candidates = [trimmed];
      if (normalized !== trimmed) candidates.push(normalized);
      for (const candidate of candidates) {
        const snap = await getDocs(query(collection(db, 'users'), where('email', '==', candidate), limit(1)));
        if (!snap.empty) {
          const d = snap.docs[0];
          return { id: d.id, ...d.data() };
        }
      }
      return null;
    },
    validateStep1() {
      if (giftAmountCents(this.form.amount) === null) {
        throw new Error('Inserisci un importo maggiore di zero, con al massimo due decimali.');
      }
      const email = this.normalizeEmail(this.form.email);
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new Error("Inserisci un'email valida per il destinatario.");
      }
      if (!this.lookup.checked || this.lookup.email !== email) {
        throw new Error('Verifica prima l’email del destinatario.');
      }
      if (!this.form.name.trim() || !this.form.surname.trim() || !this.form.phone.trim()) {
        throw new Error('Compila nome, cognome e telefono.');
      }
    },
    validateStep2() {
      if (!['paypal', 'bonifico'].includes(this.form.paymentMethod)) {
        throw new Error('Seleziona un metodo di pagamento.');
      }
    },
    nextStep() {
      try {
        if (this.step === 1) this.validateStep1();
        if (this.step === 2) this.validateStep2();
        this.error = '';
        this.step = Math.min(3, this.step + 1);
      } catch (err) {
        this.error = err?.message || 'Compila i campi richiesti.';
      }
    },
    prevStep() {
      if (this.submission || this.loading) return;
      this.error = '';
      this.step = Math.max(1, this.step - 1);
    },
    buildSubmission() {
      return {
        requestId: window.crypto.randomUUID(),
        amountCents: giftAmountCents(this.form.amount),
        paymentMethod: this.form.paymentMethod,
        recipient: {
          email: this.normalizeEmail(this.form.email),
          name: this.form.name.trim(),
          surname: this.form.surname.trim(),
          phone: this.form.phone.trim(),
        },
        message: this.form.message.trim(),
      };
    },
    async createPaypalOrder(order) {
      const { orderId, amountCents, currency } = order;
      const user = getAuth().currentUser;
      if (!user) throw new Error('Accedi per completare il pagamento.');
      const token = await user.getIdToken();
      const url = getPayPalFunctionUrl('create');
      if (!url) throw new Error('Endpoint PayPal non configurato.');
      const basePath = (process.env.BASE_URL || '/').replace(/\/?$/, '/');
      const appBaseUrl = window.location.origin + basePath;
      const returnUrl = `${appBaseUrl}#/paypal/return?orderId=${encodeURIComponent(orderId)}`;
      const cancelUrl = `${appBaseUrl}#/paypal/cancel?orderId=${encodeURIComponent(orderId)}`;

      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          orderId,
          amount: (amountCents / 100).toFixed(2),
          currency,
          userEmail: user.email,
          returnUrl,
          cancelUrl,
          items: [{ name: 'Gift card Fusco Fit', quantity: 1, unit_amount: amountCents / 100 }],
          metadata: { createdFrom: 'GiftOrderStep' },
        }),
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(errText || 'Errore durante la creazione del pagamento PayPal.');
      }
      return resp.json();
    },
    async onSubmit() {
      if (this.loading || this.completed) return;
      this.error = '';
      this.success = '';
      this.loading = true;
      try {
        if (!this.submission) {
          this.validateStep1();
          this.validateStep2();
          this.submission = this.buildSubmission();
        }
        if (!this.createdOrder) {
          const createOrder = httpsCallable(getFunctions(), 'createGiftCardOrder');
          const response = await createOrder(this.submission);
          const order = response?.data;
          if (!order?.ok || !order.orderId || !Number.isSafeInteger(order.amountCents) || order.amountCents <= 0) {
            throw new Error('Risposta ordine non valida. Riprova per recuperare lo stesso ordine.');
          }
          this.createdOrder = order;
        }
        const order = this.createdOrder;
        if (order.paymentMethod === 'paypal') {
          const payment = await this.createPaypalOrder(order);
          if (payment.alreadyPaid) {
            this.completed = true;
            this.success = 'Pagamento confermato: la gift è stata accreditata sul saldo del destinatario.';
          } else if (payment.approveLink) {
            this.success = 'Ordine creato. Reindirizzamento a PayPal…';
            window.location.href = payment.approveLink;
          } else {
            throw new Error('Link PayPal non disponibile. Riprova per riprendere lo stesso pagamento.');
          }
        } else {
          this.completed = true;
          this.success = 'Ordine registrato. Contatta il team amministrazione per ricevere i dati del bonifico. La gift sarà accreditata dopo la conferma del team.';
          await this.$router.replace({
            name: 'PublicCheckoutSuccess',
            query: { orderId: order.orderId, method: 'bonifico', type: 'gift' },
          });
        }
      } catch (err) {
        // A failed network response may hide a successfully created order: reuse its requestId.
        // Only a definitive validation failure permits editing the request again.
        if (!this.createdOrder && ['functions/invalid-argument', 'functions/not-found', 'functions/unauthenticated'].includes(err?.code)) {
          this.submission = null;
        }
        this.error = err?.message || 'Errore durante la creazione del regalo. Riprova.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* All gift styles stay inside this page, including resets for the shared app CSS. */
.fusco-gift-page {
  --fusco-gift-red: #c8102e;
  --fusco-gift-ink: #111110;
  --fusco-gift-muted: #686862;
  --fusco-gift-line: #e5e3dc;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  padding: 0;
  background: #f5f4f0;
  color: var(--fusco-gift-ink);
  font-family: 'Montserrat', Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  color-scheme: light;
}
.fusco-gift-page, .fusco-gift-page *, .fusco-gift-page *::before, .fusco-gift-page *::after { box-sizing: border-box; }
.fusco-gift-page button { appearance: none; margin: 0; border: 0; font: inherit; cursor: pointer; text-transform: none; box-shadow: none; }
.fusco-gift-page button:hover { opacity: 1; }
.fusco-gift-page button:disabled { opacity: .4; cursor: default; transform: none; }
.fusco-gift-page :is(button, input, textarea):focus-visible { outline: 3px solid #c8102e; outline-offset: 4px; }
.fusco-gift-page .fusco-gift-icon { display: inline-block; flex: none; width: 20px; height: 20px; font-size: 20px; line-height: 1; color: inherit; }
.fusco-gift-header { border-bottom: 1px solid var(--fusco-gift-line); background: #f5f4f0; }
.fusco-gift-header-inner { display: flex; align-items: center; gap: 22px; max-width: 1240px; min-height: 100px; margin: 0 auto; padding: 16px 40px; }
.fusco-gift-page .fusco-gift-back { display: grid; place-items: center; width: 38px; height: 38px; padding: 0; border: 1px solid #d9d7cf; border-radius: 50%; color: var(--fusco-gift-ink); background: transparent; }
.fusco-gift-page .fusco-gift-brand-button { flex: none; padding: 0; border-radius: 0; background: none; }
.fusco-gift-brand { display: block; position: relative; width: 113.4px; height: 57.6px; overflow: hidden; }
.fusco-gift-logo { display: block; position: absolute; left: -33.3px; top: -61.56px; width: 180px; height: 180px; max-width: none; max-height: none; object-fit: contain; }
.fusco-gift-header-label { margin-left: 12px; color: var(--fusco-gift-muted); font-size: 11px; letter-spacing: 1.7px; text-transform: uppercase; }
.fusco-gift-page .fusco-gift-account { display: flex; align-items: center; gap: 8px; margin-left: auto; padding: 10px 0; border-radius: 0; color: var(--fusco-gift-ink); background: none; font-size: 11px; font-weight: 700; }
.fusco-gift-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.08fr); align-items: start; gap: clamp(36px, 5vw, 72px); max-width: 1180px; margin: 0 auto; padding: 56px 40px 64px; }
.fusco-gift-intro { min-width: 0; }
.fusco-gift-page .fusco-gift-eyebrow { display: flex; align-items: center; gap: 9px; margin: 0; color: var(--fusco-gift-muted); font-size: 11px; font-weight: 700; letter-spacing: 1.7px; line-height: 1.5; text-transform: uppercase; }
.fusco-gift-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--fusco-gift-red); }
.fusco-gift-page .fusco-gift-title { margin: 20px 0 18px; color: var(--fusco-gift-ink); font-size: clamp(48px, 5.2vw, 70px); font-weight: 900; font-style: normal; letter-spacing: -2.8px; line-height: 1.02; text-align: left; }
.fusco-gift-title-accent { color: var(--fusco-gift-red); font-weight: 400; font-style: italic; letter-spacing: -2px; }
.fusco-gift-page .fusco-gift-lead { margin: 0; color: #585852; font-size: 15px; line-height: 1.8; }
.fusco-gift-preview { position: relative; isolation: isolate; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; min-height: 256px; margin: 30px 0 26px; padding: 26px 28px; border: 1px solid #383735; border-radius: 20px; background: #1b1b1a; color: #fff; box-shadow: 0 18px 32px -18px #17171560; }
.fusco-gift-preview::before { content: ''; position: absolute; z-index: -1; top: -85px; right: 35px; width: 54px; height: 440px; transform: rotate(32deg); background: var(--fusco-gift-red); }
.fusco-gift-preview::after { content: ''; position: absolute; z-index: -1; top: -85px; right: -52px; width: 75px; height: 440px; transform: rotate(32deg); border-left: 1px solid #ffffff3b; border-right: 1px solid #ffffff1a; }
.fusco-gift-preview-top, .fusco-gift-preview-bottom { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.fusco-gift-preview-brand { font-size: 12px; font-weight: 700; letter-spacing: 2px; }
.fusco-gift-preview-tag { padding: 4px 8px; border: 1px solid #ffffff59; border-radius: 4px; font-size: 11px; letter-spacing: 1.2px; white-space: nowrap; }
.fusco-gift-page .fusco-gift-preview-motto { margin: 22px 0; color: #fff; font-size: clamp(32px, 3.7vw, 48px); font-weight: 900; line-height: .94; letter-spacing: -1.8px; }
.fusco-gift-preview-motto-accent { font-style: italic; }
.fusco-gift-preview-bottom { align-items: end; }
.fusco-gift-preview-dedication { min-width: 0; }
.fusco-gift-preview-caption { display: block; margin-bottom: 4px; color: #c6c5bf; font-size: 11px; letter-spacing: .5px; }
.fusco-gift-preview-recipient { display: block; font-size: 12px; overflow-wrap: anywhere; }
.fusco-gift-preview-value { max-width: 65%; font-size: clamp(18px, 2vw, 26px); font-weight: 700; line-height: 1.2; text-align: right; overflow-wrap: anywhere; }
.fusco-gift-preview-value--placeholder { max-width: 45%; font-size: 11px; font-weight: 400; }
.fusco-gift-credit-note { display: flex; align-items: start; gap: 14px; }
.fusco-gift-page .fusco-gift-credit-icon { display: grid; place-items: center; width: 38px; height: 38px; border: 1px solid #dedbd2; border-radius: 12px; color: var(--fusco-gift-red); }
.fusco-gift-page .fusco-gift-credit-title { margin: 0 0 5px; color: var(--fusco-gift-ink); font-size: 12px; font-weight: 700; line-height: 1.6; text-align: left; }
.fusco-gift-page .fusco-gift-credit-text { margin: 0; color: var(--fusco-gift-muted); font-size: 12px; line-height: 1.7; }
.fusco-gift-form { display: block; min-width: 0; width: 100%; margin: 0; padding: 30px; border: 1px solid #eae8e1; border-radius: 24px; background: #fff; box-shadow: 0 18px 56px -30px #302d2829; text-align: left; }
.fusco-gift-form-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.fusco-gift-step-count { color: #89877f; font-size: 11px; letter-spacing: 2px; }
.fusco-gift-steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 24px 0 30px; padding: 0; list-style: none; }
.fusco-gift-step { display: flex; align-items: center; gap: 7px; min-width: 0; padding: 0 0 14px; border-bottom: 2px solid #eeeae4; color: #87847b; }
.fusco-gift-step-number { display: grid; place-items: center; flex: none; width: 25px; height: 25px; border: 1px solid var(--fusco-gift-line); border-radius: 50%; font-size: 11px; font-weight: 700; }
.fusco-gift-step-label { font-size: 11px; }
.fusco-gift-step--active { border-color: var(--fusco-gift-red); color: var(--fusco-gift-red); font-weight: 700; }
.fusco-gift-step--active .fusco-gift-step-number { border-color: var(--fusco-gift-red); background: var(--fusco-gift-red); color: #fff; }
.fusco-gift-step--done { border-color: #d2949f; color: #5e504f; }
.fusco-gift-step--done .fusco-gift-step-number { border-color: #f3d6dc; background: #fff3f5; color: var(--fusco-gift-red); }
.fusco-gift-page .fusco-gift-step-number .fusco-gift-icon { width: 14px; height: 14px; font-size: 14px; }
.fusco-gift-section { display: flex; flex-direction: column; gap: 22px; }
.fusco-gift-page .fusco-gift-section-title { margin: 0 0 8px; color: var(--fusco-gift-ink); font-size: 22px; font-style: normal; font-weight: 700; letter-spacing: -.6px; line-height: 1.35; text-align: left; }
.fusco-gift-page .fusco-gift-section-description { margin: 0; color: var(--fusco-gift-muted); font-size: 12px; line-height: 1.65; }
.fusco-gift-field { display: flex; flex-direction: column; min-width: 0; gap: 8px; }
.fusco-gift-page .fusco-gift-label { display: block; margin: 0; padding: 0; color: #33332e; font-size: 12px; font-weight: 700; line-height: 1.5; }
.fusco-gift-optional { font-weight: 400; }
.fusco-gift-page .fusco-gift-input { display: block; width: 100%; min-width: 0; max-width: 100%; height: 50px; margin: 0; padding: 12px 14px; border: 1px solid #dcdad2; border-radius: 10px; background: #faf9f6; color: #111110; font-family: inherit; font-size: 13px; font-weight: 400; line-height: 1.5; box-shadow: none; }
.fusco-gift-page .fusco-gift-input::placeholder { color: #908e85; opacity: 1; }
.fusco-gift-page .fusco-gift-input:focus { border-color: var(--fusco-gift-red); background: #fff; }
.fusco-gift-amount-field { display: flex; align-items: center; gap: 12px; padding: 0 18px; border: 1px solid #dcdad2; border-radius: 12px; background: #faf9f6; }
.fusco-gift-amount-field:focus-within { border-color: var(--fusco-gift-red); }
.fusco-gift-currency { color: var(--fusco-gift-red); font-size: 28px; font-weight: 400; }
.fusco-gift-page .fusco-gift-input--amount { height: 70px; padding: 8px 0; border: 0; border-radius: 0; background: transparent; font-size: 32px; font-weight: 700; letter-spacing: -.7px; }
.fusco-gift-page .fusco-gift-input--amount:focus { background: transparent; }
.fusco-gift-page .fusco-gift-input--amount:focus-visible { outline-offset: 0; }
.fusco-gift-page .fusco-gift-hint { display: block; margin: 0; color: var(--fusco-gift-muted); font-size: 11px; font-weight: 400; line-height: 1.7; overflow-wrap: anywhere; }
.fusco-gift-page .fusco-gift-hint--ok { color: #237443; }
.fusco-gift-page .fusco-gift-hint--warn { color: #966211; }
.fusco-gift-recipient-field { padding-top: 20px; border-top: 1px solid var(--fusco-gift-line); }
.fusco-gift-inline { display: flex; gap: 8px; align-items: stretch; }
.fusco-gift-recipient-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 12px; }
.fusco-gift-field--full { grid-column: 1 / -1; }
.fusco-gift-page .fusco-gift-button { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 46px; padding: 12px 20px; border: 1px solid transparent; border-radius: 100px; color: var(--fusco-gift-ink); background: transparent; font-size: 12px; font-weight: 700; line-height: 1.4; transition: background-color .18s, border-color .18s; }
.fusco-gift-page .fusco-gift-button--primary { padding-right: 22px; padding-left: 24px; background: var(--fusco-gift-red); color: #fff; }
.fusco-gift-page .fusco-gift-button--primary:hover:not(:disabled) { background: #a80d26; }
.fusco-gift-page .fusco-gift-button--secondary { border-color: #dcdad2; background: #fff; }
.fusco-gift-page .fusco-gift-button--secondary:hover:not(:disabled) { border-color: #aaa59a; background: #f5f4f0; }
.fusco-gift-page .fusco-gift-button--back { padding-right: 8px; padding-left: 0; color: #53534c; }
.fusco-gift-page .fusco-gift-lookup { flex: none; padding: 10px 14px; border-radius: 10px; font-size: 11px; }
.fusco-gift-page .fusco-gift-textarea { height: auto; min-height: 124px; resize: vertical; }
.fusco-gift-payment-fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.fusco-gift-payment-options { display: grid; gap: 10px; margin-top: 10px; }
.fusco-gift-page .fusco-gift-payment { display: flex; align-items: center; gap: 12px; margin: 0; padding: 16px; border: 1px solid #dcdad2; border-radius: 12px; background: #faf9f6; color: var(--fusco-gift-ink); cursor: pointer; }
.fusco-gift-page .fusco-gift-payment--active { border-color: var(--fusco-gift-red); background: #fff7f8; box-shadow: inset 0 0 0 1px var(--fusco-gift-red); }
.fusco-gift-page .fusco-gift-radio { flex: none; width: 16px; height: 16px; margin: 0; padding: 0; accent-color: var(--fusco-gift-red); }
.fusco-gift-payment-copy { flex: 1; min-width: 0; }
.fusco-gift-payment-title { display: block; margin-bottom: 3px; font-size: 13px; font-weight: 700; }
.fusco-gift-payment-description { display: block; color: var(--fusco-gift-muted); font-size: 11px; font-weight: 400; }
.fusco-gift-page .fusco-gift-payment-icon { color: #8a837b; }
.fusco-gift-summary { margin: 0; padding: 0; }
.fusco-gift-summary-row { display: grid; grid-template-columns: 90px minmax(0, 1fr); gap: 16px; padding: 13px 0; border-bottom: 1px solid #efede7; }
.fusco-gift-summary-label { color: var(--fusco-gift-muted); font-size: 11px; font-weight: 400; }
.fusco-gift-summary-value { min-width: 0; margin: 0; color: var(--fusco-gift-ink); font-size: 12px; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.fusco-gift-summary-email { display: block; margin-top: 4px; color: var(--fusco-gift-muted); font-size: 11px; font-weight: 400; }
.fusco-gift-summary-row--total { align-items: center; }
.fusco-gift-summary-row--total .fusco-gift-summary-value { color: var(--fusco-gift-red); font-size: 28px; }
.fusco-gift-summary-row--message { grid-template-columns: 1fr; gap: 8px; }
.fusco-gift-summary-row--message .fusco-gift-summary-value { font-style: italic; font-weight: 400; text-align: left; white-space: pre-wrap; }
.fusco-gift-actions { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-top: 28px; padding-top: 22px; border-top: 1px solid var(--fusco-gift-line); }
.fusco-gift-alert { margin-top: 18px; padding: 14px; border: 1px solid; border-radius: 12px; font-size: 12px; line-height: 1.7; overflow-wrap: anywhere; }
.fusco-gift-alert--error { background: #fff2f3; border-color: #f2c8d0; color: #a00e27; }
.fusco-gift-alert--success { background: #eff9f1; border-color: #c2e3cb; color: #236b38; }
.fusco-gift-page .fusco-gift-form-note { display: flex; align-items: center; justify-content: center; gap: 7px; margin: 22px 0 0; color: #89867d; font-size: 11px; }
.fusco-gift-page .fusco-gift-form-note .fusco-gift-icon { width: 15px; height: 15px; font-size: 15px; }
.fusco-gift-footer { display: flex; justify-content: space-between; gap: 24px; max-width: 1100px; margin: 0 auto; padding: 22px 0 28px; border-top: 1px solid var(--fusco-gift-line); color: #79776d; font-size: 11px; }
.fusco-gift-footer-brand { color: #4d4b43; font-weight: 700; letter-spacing: 2px; }
@media (min-width: 1400px) { .fusco-gift-layout { padding-top: 72px; padding-bottom: 80px; } }
@media (max-width: 1100px) {
  .fusco-gift-header-inner { padding-right: 28px; padding-left: 28px; }
  .fusco-gift-layout { gap: 30px; padding: 40px 28px 48px; }
  .fusco-gift-form { padding: 24px; }
  .fusco-gift-preview { padding: 24px; }
  .fusco-gift-steps { gap: 8px; }
  .fusco-gift-step { flex-direction: column; align-items: start; gap: 7px; }
  .fusco-gift-footer { margin-right: 28px; margin-left: 28px; }
}
@media (max-width: 760px) {
  .fusco-gift-header-inner { min-height: 78px; gap: 16px; padding: 12px 20px; }
  .fusco-gift-brand { width: 90.72px; height: 46.08px; }
  .fusco-gift-logo { width: 144px; height: 144px; left: -26.64px; top: -49.248px; }
  .fusco-gift-header-label { display: none; }
  .fusco-gift-layout { grid-template-columns: minmax(0, 1fr); gap: 28px; max-width: 560px; padding: 30px 20px 40px; }
  .fusco-gift-page .fusco-gift-title { margin-top: 14px; font-size: 54px; }
  .fusco-gift-page .fusco-gift-lead { font-size: 14px; }
  .fusco-gift-preview { min-height: 228px; margin: 24px 0 20px; }
  .fusco-gift-page .fusco-gift-preview-motto { font-size: 44px; }
  .fusco-gift-preview-value { font-size: 26px; }
  .fusco-gift-preview-value--placeholder { font-size: 11px; }
  .fusco-gift-step { flex-direction: row; align-items: center; }
  .fusco-gift-form { padding: 24px; border-radius: 20px; }
  .fusco-gift-page .fusco-gift-input { font-size: 16px; }
  .fusco-gift-page .fusco-gift-input--amount { font-size: 32px; }
  .fusco-gift-footer { margin: 0 20px; font-size: 11px; }
}
@media (max-width: 390px) {
  .fusco-gift-account-label { display: none; }
  .fusco-gift-form { padding: 20px 16px; }
  .fusco-gift-step { flex-direction: column; align-items: start; }
  .fusco-gift-recipient-grid { grid-template-columns: minmax(0, 1fr); }
  .fusco-gift-inline { flex-wrap: wrap; }
  .fusco-gift-page .fusco-gift-lookup { width: 100%; min-height: 40px; }
  .fusco-gift-actions { gap: 10px; }
  .fusco-gift-page .fusco-gift-button--primary { padding-right: 16px; padding-left: 16px; }
}
@media (prefers-reduced-motion: reduce) { .fusco-gift-page .fusco-gift-button { transition: none; } }
</style>
